/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicTreeMapLayerSpec } from '../../../../layer/preset/treeMap/dynamicTreeMap';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

type DynamicTreeMapLayerType = StandardChartLayerSpec & DynamicTreeMapLayerSpec & ILayerTypeSpec;

// 创建动态矩形树图数据类型验证器
const validateDynamicTreeMapDataTypes = createDataTypeValidator('dynamicTreeMap', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' },
  { field: 'group', type: ['string', 'date'] },
  { field: 'color', type: 'number', required: false }
]);

// 创建动态矩形树图编码验证器
const validateDynamicTreeMapEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'group' },
  { field: 'color', required: false }
]);

/**
 * 验证时间维度数据是否合法
 * @param layer 图层配置
 * @param data 数据
 */
function validateTimelineDimension(layer: DynamicTreeMapLayerType, data: Data) {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return;
  }

  // 提取时间维度数据
  const groupField = encoding.group;
  const timePoints = [...new Set(values.map(item => item[groupField]))];

  if (timePoints.length < 1) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires at least 1 time point in the "group" field`
    );
    return;
  }

  // 检查每个时间点是否有足够的数据
  timePoints.forEach(timePoint => {
    const timePointData = values.filter(item => item[groupField] === timePoint);
    if (timePointData.length < 1) {
      utils.logger.warn(
        'warning',
        `Time point "${timePoint}" in the "${layer.type}" layer must have at least 1 data point`
      );
      layer.__isEncodingValid = false;
    }
  });
}

// 额外的检查函数
const additionalDynamicTreeMapChecks = (layer: DynamicTreeMapLayerType, data: Data) => {
  // 验证时间维度数据
  validateTimelineDimension(layer, data);
};

// 创建并导出动态矩形树图处理器
export const dynamicTreeMap = createLayerProcessor<DynamicTreeMapLayerType>(
  'dynamicTreeMap',
  validateDynamicTreeMapEncoding,
  validateDynamicTreeMapDataTypes,
  additionalDynamicTreeMapChecks
);
